import React, { Fragment, memo, useEffect, useState } from 'react';

import { Box, Grid, Typography } from '@material-ui/core';

import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { CardComponent, PageHeader } from 'components';
import { getTWithPathKey } from 'utils';
import { picturesModel, socialIntegrationModel } from 'models';
import { PaginationParams, Image } from 'types';
import { SocialMediaTypes } from 'types/enums';
import { VideoAsset } from 'types/other';
import { withPagination } from 'utils/enhancers';
import { ButtonComponent } from 'components/ButtonComponent/ButtonComponent';
import { DropzoneImage } from 'components/DropzoneImage/DropzoneImage';
import { DropzoneVideo } from 'components/DropzoneVideo/DropzoneVideo';
import { videosModel } from 'models/VideosModel';

export interface WithPaginationProps {
  setPaginationItemsCount: Function;
  resetPaginationPage: Function;
  paginationParams: PaginationParams;
}

const ManageSocialContentComponent = ({
  setPaginationItemsCount,
  resetPaginationPage,
  paginationParams,
}: WithPaginationProps) => {
  const [posts, setPosts] = useState<Array<Image | VideoAsset>>([]);
  const [currentType, setCurrentType] = useState(SocialMediaTypes.pictures);
  const { handleServerError } = useServerErrorActions();
  const { withPageProgressHandler } = useBusyProgressActions();
  const getT = getTWithPathKey('pages.socialContent');

  useEffect(() => {
    getPosts();
  }, [paginationParams, currentType]);

  const getPosts = withPageProgressHandler(() =>
    currentType === SocialMediaTypes.pictures
      ? socialIntegrationModel
          .getPosts({ ...paginationParams, instagram: true })
          .then(data => {
            setPosts(data.pictures);
            setPaginationItemsCount(data.count);
          })
          .catch(handleServerError)
      : socialIntegrationModel
          .getVideos({ ...paginationParams, instagram: true })
          .then(data => {
            setPosts(data.videos);
            setPaginationItemsCount(data.count);
          })
          .catch(handleServerError),
  );

  const handleTabClick = (type: SocialMediaTypes) => {
    resetPaginationPage();
    setCurrentType(type);
  };

  const handleEditImage = (id: number, data: any) =>
    picturesModel.editPicture(id, data).then(getPosts).catch(handleServerError);

  const handleEditVideo = (imageId: number, data: Image) =>
    videosModel
      .updateVideo(imageId, {
        seoDesc: data.accessibilityDescription ?? '',
        seoTitle: data.seoTitle ?? data.imageName,
      })
      .then(getPosts)
      .catch(handleServerError);

  const renderImageList = () => (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      <Box>
        <Typography>{getT('hint')}</Typography>
      </Box>
      <Box>
        {posts.map((post, index) => (
          <Fragment key={post.id}>
            {currentType === SocialMediaTypes.pictures ? (
              <DropzoneImage
                image={post}
                onChangeSelected={() => {}}
                selectedImages={[]}
                withCheckbox={false}
                status={null}
                withEdit
                handleEdit={handleEditImage}
              />
            ) : (
              <DropzoneVideo
                image={{ ...post, accessibilityDescription: (post as VideoAsset).seoDesc }}
                withCheckbox={false}
                status={null}
                withEdit
                handleEdit={handleEditVideo}
                withLink={false}
              />
            )}
          </Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <PageHeader title={getT('title')} />
      <Box mb={5}>
        <ButtonComponent
          color={currentType === SocialMediaTypes.pictures ? 'primary' : undefined}
          text="Image posts"
          onClick={() => handleTabClick(SocialMediaTypes.pictures)}
          style={{ marginRight: '10px' }}
        />
        <ButtonComponent
          color={currentType === SocialMediaTypes.videos ? 'primary' : undefined}
          text="Video posts"
          onClick={() => handleTabClick(SocialMediaTypes.videos)}
        />
      </Box>
      <Grid container spacing={6}>
        <CardComponent sm={12}>
          <Grid container>
            {!posts?.length && <Typography>{getT('noPostsMessage')}</Typography>}
            {!!posts?.length && renderImageList()}
          </Grid>
        </CardComponent>
      </Grid>
    </>
  );
};

export const ManageSocialContent = memo(withPagination(ManageSocialContentComponent));
